<template class="page-content">
  <ModalLayout ref="bulkUpdateModal" modal-id="bulk-update-modal"
    :loading="isSavableStateLoading"
    @close="modalClosed">
    <template #modal-header>
      <h5 class="modal-title">
        Bulk Status Update
      </h5>
    </template>
    <template #modal-body>
      <form @submit.prevent="saveItems">
        <fieldset>
          <div class="row">
            <div class="col-sm-12">
              <div class="row">
                <MultiSelectInput v-model="selectedStatus"
                  name="status"
                  label="Status"
                  :options="statuses"
                  append-to="self"
                  label-key="label"
                  value-key="code"
                  :multiple="false"
                  col-style="form-group-5-column-large col-xl-5" />
              </div>
              <ActionToolbar :component-state="currentState" 
                toolbar-size="col-md-12"
                :error-message="formError" 
                :success-message="successMessage" 
                primary-button-text="Update"
                secondary-button-text="Cancel"
                @secondary-click="cancel" />
            </div>
          </div>
        </fieldset>
      </form>
    </template>
  </ModalLayout>
</template>

<script setup lang="ts">
import { ref } from 'vue';
// @ts-ignore
import ModalLayout from "@/src/components/shared/ModalLayout.vue";
// @ts-ignore
import ActionToolbar from "@/src/components/shared/ActionToolbar.vue";
// @ts-ignore
import MultiSelectInput from "@/src/components/shared/FormComponents/MultiSelectInput.vue";
// @ts-ignore
import { useChecklistStore } from '@/src/stores/checklist';
import { useSavableState } from '@/src/composables/useSavableState';
import { useModalActions } from '@/src/composables/useModalActions';

const props = defineProps({
  items: { required: true, type: Array as () => any[] },
  statuses: { required: true, type: Array as () => any[] },
});

const emit = defineEmits(['close', 'updated']);

const checklistStore = useChecklistStore();

const {
  formError,
  currentState,
  isSavableStateLoading,
  setSavableStateBlank,
  setSavableStateProcessing,
  setSavableStateSuccess,
  handleFormError,
} = useSavableState();

// Template refs
const bulkUpdateModal = ref<InstanceType<typeof ModalLayout> | null>(null);

// Component state
const selectedStatus = ref<string | null>(null);
const successMessage = ref<string | null>(null);

const { showModal, closeModal, closeAllModals } = useModalActions(bulkUpdateModal, emit);

function modalClosed() {
  setSavableStateBlank();
  selectedStatus.value = null;
  emit('close');
}

function cancel() {
  modalClosed();
  ($ as any)((bulkUpdateModal.value as any).$el).modal('hide');
}

async function saveItems() {
  if(selectedStatus.value != null) {
    const statusLabel = props.statuses.find((s: any) => s.code === selectedStatus.value)?.label ?? selectedStatus.value;
    if(!confirm(`Are you sure you want to update selected ${props.items.length} item(s) with status ${statusLabel} ?`)) {
      return;
    }
  }
  setSavableStateProcessing();
  const payload = {
    status: selectedStatus.value,
    item_ids: props.items.map((item: any) => item.id)
  };

  try {
    await checklistStore.updateStatus(payload);
    selectedStatus.value = null;
    setSavableStateSuccess();
    successMessage.value = `Updated status of ${props.items.length} items`;
    emit('updated');
  } catch (error) {
    handleFormError(error);
  }
}

defineExpose({ showModal, closeModal, closeAllModals });
</script>
<style>
  #bulk-update-modal .modal-body, .modal-content {
    overflow: visible !important;
  }
</style>
